import { Save, Trash2, X } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "src/components/ui/alert-dialog";
import { AnimatedDialog, AnimatedDialogContent, AnimatedDialogHeader, AnimatedDialogTitle } from "src/components/ui/animated-dialog";
import { Button } from "src/components/ui/button";
import BuildkiteIcon from "src/components/ui/icons/buildkite";
import GithubIcon from "src/components/ui/icons/github";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/ui/select";
import { Textarea } from "src/components/ui/textarea";
import { UIEditSection } from "src/components/ui/UIEditSection";
import { ReviewSection } from "src/components/wizard";
import { useToast } from "src/hooks/use-toast";

import {
  addNestedParameter,
  createParameter,
  type Parameter,
  parametersToSchema,
  removeParameter,
  schemaToParameters,
  updateParameter,
} from "../utils/parameterSchema";
import type { EditCapabilityDialogDeleteMutation } from "./__generated__/EditCapabilityDialogDeleteMutation.graphql";
import type { EditCapabilityDialogQuery } from "./__generated__/EditCapabilityDialogQuery.graphql";
import type { EditCapabilityDialogUpdateMutation } from "./__generated__/EditCapabilityDialogUpdateMutation.graphql";
import { ParameterTable } from "./ParameterTable";

const editCapabilityDialogQuery = graphql`
  query EditCapabilityDialogQuery($capabilityId: GlobalID!) {
    node(id: $capabilityId) {
      ... on GraphQLCapability {
        id
        name
        description
        approvalStyle
        capabilityType
        targetConfig
        parametersSchema
      }
    }
  }
`;

const updateCapabilityMutation = graphql`
  mutation EditCapabilityDialogUpdateMutation($input: UpdateCapabilityInput!) {
    updateCapability(input: $input) {
      capability {
        id
        name
        description
        approvalStyle
        parametersSchema
      }
      error
    }
  }
`;

const deleteCapabilityMutation = graphql`
  mutation EditCapabilityDialogDeleteMutation($input: DeleteCapabilityInput!) {
    deleteCapability(input: $input) {
      success
      error
    }
  }
`;

const APPROVAL_TYPE_OPTIONS = [
  { value: "MANUAL", label: "Requires Approval" },
  { value: "AUTOMATIC", label: "Automatic" },
];

const CAPABILITY_TYPE_LABELS: Record<string, string> = {
  GITHUB_ACTIONS_WORKFLOW: "GitHub Actions Workflow",
  BUILDKITE_PIPELINE: "Buildkite Pipeline",
};

interface EditCapabilityDialogProps {
  capabilityId: string;
  isOpen: boolean;
  onClose: () => void;
  onDeleted?: () => void;
}

export function EditCapabilityDialog({
  capabilityId,
  isOpen,
  onClose,
  onDeleted,
}: EditCapabilityDialogProps) {
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const data = useLazyLoadQuery<EditCapabilityDialogQuery>(
    editCapabilityDialogQuery,
    { capabilityId },
    { fetchPolicy: "network-only" }
  );

  const capability = data.node;

  const [commitUpdate] = useMutation<EditCapabilityDialogUpdateMutation>(updateCapabilityMutation);
  const [commitDelete, isDeleting] = useMutation<EditCapabilityDialogDeleteMutation>(deleteCapabilityMutation);

  // Form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [approvalType, setApprovalType] = useState<string>("MANUAL");
  const [parameters, setParameters] = useState<Parameter[]>([]);
  const [expandedParams, setExpandedParams] = useState<Set<string>>(new Set());
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  // Initialize form state when dialog opens
  useEffect(() => {
    if (isOpen && capability) {
      setName(capability.name || "");
      setDescription(capability.description || "");
      setApprovalType(capability.approvalStyle || "MANUAL");
      setParameters(schemaToParameters(capability.parametersSchema as Record<string, unknown>));
      setExpandedParams(new Set());
      setShowValidationErrors(false);
    }
  }, [isOpen, capability]);

  // Parameter handlers
  const handleAddParameter = useCallback(() => {
    setParameters((prev) => [...prev, createParameter()]);
  }, []);

  const handleAddNestedParameter = useCallback((parentId: string) => {
    setParameters((prev) => addNestedParameter(prev, parentId));
  }, []);

  const handleRemoveParameter = useCallback((id: string) => {
    setParameters((prev) => removeParameter(prev, id));
  }, []);

  const handleParameterChange = useCallback((id: string, field: keyof Parameter, value: unknown) => {
    setParameters((prev) => updateParameter(prev, id, field, value));
  }, []);

  const toggleExpanded = useCallback((paramId: string) => {
    setExpandedParams((prev) => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(paramId)) {
        newExpanded.delete(paramId);
      } else {
        newExpanded.add(paramId);
      }
      return newExpanded;
    });
  }, []);

  const handleSave = () => {
    setShowValidationErrors(true);

    if (!name.trim()) {
      return;
    }

    setIsSaving(true);

    const parametersSchema = parametersToSchema(parameters);

    commitUpdate({
      variables: {
        input: {
          id: capabilityId,
          name: name.trim(),
          description: description.trim(),
          approvalStyle: approvalType as "MANUAL" | "AUTOMATIC",
          parametersSchema,
        },
      },
      onCompleted: (response) => {
        setIsSaving(false);
        if (response.updateCapability.error) {
          toast({
            title: "Error updating capability",
            description: response.updateCapability.error,
            variant: "error",
          });
        } else if (response.updateCapability.capability) {
          toast({
            title: "Capability updated",
            description: `"${response.updateCapability.capability.name}" has been updated successfully.`,
            variant: "success",
          });
          onClose();
        }
      },
      onError: (error) => {
        setIsSaving(false);
        toast({
          title: "Error updating capability",
          description: error.message || "An unexpected error occurred",
          variant: "error",
        });
      },
    });
  };

  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    commitDelete({
      variables: {
        input: {
          id: capabilityId,
        },
      },
      onCompleted: (response) => {
        if (response.deleteCapability.error) {
          toast({
            title: "Error",
            description: response.deleteCapability.error,
            variant: "error",
          });
        } else if (response.deleteCapability.success) {
          toast({
            title: "Capability deleted",
            description: `"${name}" has been deleted successfully.`,
            variant: "success",
          });
          setIsDeleteDialogOpen(false);
          onClose();
          onDeleted?.();
        }
      },
      onError: (error) => {
        toast({
          title: "Error",
          description: error.message || "An unexpected error occurred",
          variant: "error",
        });
        setIsDeleteDialogOpen(false);
      },
    });
  };

  if (!capability) {
    return null;
  }

  // Extract pipeline details from targetConfig
  const targetConfig = (capability.targetConfig as Record<string, unknown>) || {};
  const capabilityType = capability.capabilityType || "GITHUB_ACTIONS_WORKFLOW";

  const getPipelineDetails = () => {
    if (capabilityType === "GITHUB_ACTIONS_WORKFLOW") {
      return {
        type: "GitHub Actions Workflow",
        repository: (targetConfig.repo_full_name as string) || "Unknown",
        workflow: (targetConfig.workflow_name as string) || (targetConfig.workflow_id as string) || "Unknown",
        branch: (targetConfig.ref as string) || "main",
      };
    }
    return {
      type: "Buildkite Pipeline",
      organization: (targetConfig.org_slug as string) || "Unknown",
      pipeline: (targetConfig.pipeline_slug as string) || "Unknown",
      branch: (targetConfig.branch as string) || "main",
    };
  };

  const pipelineDetails = getPipelineDetails();

  return (
    <AnimatedDialog open={isOpen} onOpenChange={onClose}>
      <AnimatedDialogContent variant="full" dismissOnOutsideClick={false}>
        <AnimatedDialogHeader>
          <AnimatedDialogTitle className="text-foreground text-xl">Edit Capability</AnimatedDialogTitle>
        </AnimatedDialogHeader>

        <div className="space-y-6 max-h-[70vh] overflow-y-auto mt-2 p-2">
          {/* Name - Primary editable field */}
          <div>
            <Label htmlFor="capability-name" className="text-sm font-medium text-secondary-foreground">
              Name
              <span className="text-red-500 ml-1">*</span>
            </Label>
            <Input
              id="capability-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter capability name"
              className={`bg-card border-border text-foreground mt-2 ${
                showValidationErrors && !name.trim() ? "border-red-500" : ""
              }`}
            />
            <p className="text-xs text-muted-foreground mt-2">
              Give your capability a descriptive name to identify it easily.
            </p>
          </div>

          {/* Pipeline Details Section - Read only */}
          <ReviewSection title="Pipeline Details">
            <div className="py-3 ">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Type</label>
              <div className="mt-1 flex items-center gap-2">
                {capabilityType === "GITHUB_ACTIONS_WORKFLOW" ? (
                  <GithubIcon className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <BuildkiteIcon className="h-4 w-4 text-muted-foreground" />
                )}
                <span>{CAPABILITY_TYPE_LABELS[capabilityType] || capabilityType}</span>
              </div>
            </div>
            {capabilityType === "GITHUB_ACTIONS_WORKFLOW" && (
              <>
                <div className="py-3 ">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Repository</label>
                  <div className="mt-1 text-muted-foreground">{pipelineDetails.repository}</div>
                </div>
                <div className="py-3 ">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Workflow</label>
                  <div className="mt-1 text-muted-foreground">{pipelineDetails.workflow}</div>
                </div>
                <div className="py-3">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Branch</label>
                  <div className="mt-1 text-muted-foreground">{pipelineDetails.branch}</div>
                </div>
              </>
            )}
            {capabilityType === "BUILDKITE_PIPELINE" && (
              <>
                <div className="py-3 ">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Organization</label>
                  <div className="mt-1 text-muted-foreground">{pipelineDetails.organization}</div>
                </div>
                <div className="py-3 ">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Pipeline</label>
                  <div className="mt-1 text-muted-foreground">{pipelineDetails.pipeline}</div>
                </div>
                <div className="py-3">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Branch</label>
                  <div className="mt-1 text-muted-foreground">{pipelineDetails.branch}</div>
                </div>
              </>
            )}
          </ReviewSection>

          {/* Parameters - Expandable */}
          <UIEditSection
            title="Parameters"
            summary={
              <span className="text-muted-foreground">
                {parameters.length === 0
                  ? "No parameters defined"
                  : `${parameters.length} parameter${parameters.length === 1 ? "" : "s"} defined`}
              </span>
            }
          >
            <ParameterTable
              parameters={parameters}
              expandedParams={expandedParams}
              onToggleExpand={toggleExpanded}
              onParameterChange={handleParameterChange}
              onRemoveParameter={handleRemoveParameter}
              onAddNestedParameter={handleAddNestedParameter}
              onAddParameter={handleAddParameter}
              columnWidths={{
                name: "w-[180px]",
                type: "w-[100px]",
                required: "w-[60px]",
                default: "w-[100px]",
                actions: "w-[40px]",
              }}
            />
          </UIEditSection>

          {/* Description - Expandable */}
          <UIEditSection
            title="Description"
            summary={
              <span className="text-muted-foreground truncate max-w-[400px] block">
                {description || "No description provided"}
              </span>
            }
          >
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what this capability does..."
              rows={4}
              className="font-mono text-sm bg-card border-border"
            />
          </UIEditSection>

          {/* Approval Type - Expandable */}
          <UIEditSection
            title="Approval Type"
            summary={
              <span className="text-muted-foreground">
                {APPROVAL_TYPE_OPTIONS.find((opt) => opt.value === approvalType)?.label || approvalType}
              </span>
            }
          >
            <Select value={approvalType} onValueChange={setApprovalType}>
              <SelectTrigger className="bg-card border-border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {APPROVAL_TYPE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </UIEditSection>
        </div>

        <div className="flex justify-between mt-6">
          <Button
            onClick={handleDeleteClick}
            disabled={isSaving || isDeleting}
            className="bg-red-600 hover:bg-red-700 text-white border-red-600"
          >
            <Trash2 className="mr-1" size={16} />
            Delete Capability
          </Button>

          <div className="flex space-x-3">
            <Button variant="outline" onClick={onClose} disabled={isSaving || isDeleting}>
              <X className="mr-1" size={16} />
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving || isDeleting} loading={isSaving} loadingText="Saving...">
              <Save className="mr-1" size={16} />
              Save
            </Button>
          </div>
        </div>
      </AnimatedDialogContent>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent className="bg-popover text-foreground border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">Confirm deletion</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              Are you sure you want to delete "{name || "this capability"}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="bg-transparent text-foreground border-border hover:bg-card hover:text-foreground mt-2 sm:mt-0"
              disabled={isDeleting}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <div className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full mr-2" />
                  Deleting...
                </>
              ) : (
                <>Delete</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AnimatedDialog>
  );
}
